import {clsx, type ClassValue} from 'clsx';

type AttributeValue = unknown;
export type BindingObject = Record<string, AttributeValue>;
export type BindingInput = ClassValue | BindingObject;
export type MergeBindingsOptions = {
    inputs: readonly BindingInput[];
};

const BINDING_PREFIXES = [':', '@', 'x-', 'data-', 'aria-'];

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
    value !== null
    && typeof value === 'object'
    && !Array.isArray(value)
);

// Distinguish DOM/Alpine binding objects from className dictionary inputs.
// Example:
// mergeBindings({inputs: ['p-2', {rounded: true}, {':data-state': () => 'open'}]})
// returns {class: 'p-2 rounded', ':data-state': () => 'open'}.
const isBindingObject = (value: Record<string, unknown>): boolean => (
    Object.keys(value).some((key) => (
        key === 'class'
        || key === 'style'
        || key === 'id'
        || key === 'role'
        || key === 'type'
        || key === 'tabindex'
        || BINDING_PREFIXES.some((prefix) => key.startsWith(prefix))
    ))
);

const mergeBinding = (target: BindingObject, input: BindingObject, classes: ClassValue[]): void => {
    Object.entries(input).forEach(([key, value]) => {
        if (key === 'class') {
            classes.push(value as ClassValue);
            return;
        }

        if (value === undefined || value === null || value === false) {
            delete target[key];
            return;
        }

        target[key] = value;
    });
};

export const mergeBindings = ({inputs}: MergeBindingsOptions): BindingObject => {
    const output: BindingObject = {};
    const classes: ClassValue[] = [];

    inputs.forEach((input) => {
        if (!input) return;

        if (isPlainObject(input) && isBindingObject(input)) {
            mergeBinding(output, input, classes);
            return;
        }

        classes.push(input as ClassValue);
    });

    const mergedClass = clsx(classes);
    if (mergedClass) output.class = mergedClass;

    return output;
};
